import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

type ImportType = 'csv' | 'excel' | 'api';

interface ImportStatus {
  status: 'idle' | 'uploading' | 'processing' | 'success' | 'error';
  message?: string;
  imported?: number;
  failed?: number;
}

export function DataImport() {
  const [activeTab, setActiveTab] = useState<ImportType>('csv');
  const [importStatus, setImportStatus] = useState<ImportStatus>({ status: 'idle' });
  const [apiEndpoint, setApiEndpoint] = useState('');
  const [apiKey, setApiKey] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', activeTab);
      return apiClient.post('/sites/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onMutate: () => {
      setImportStatus({ status: 'uploading', message: 'UPLOADING FILE...' });
    },
    onSuccess: (response) => {
      setImportStatus({
        status: 'success',
        message: 'IMPORT COMPLETE',
        imported: response.data.imported,
        failed: response.data.failed,
      });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary'] });
    },
    onError: (error: any) => {
      setImportStatus({
        status: 'error',
        message: error.response?.data?.detail || 'IMPORT FAILED',
      });
    },
  });

  const apiImportMutation = useMutation({
    mutationFn: async () => {
      return apiClient.post('/sites/import/api', {
        endpoint: apiEndpoint,
        api_key: apiKey,
      });
    },
    onMutate: () => {
      setImportStatus({ status: 'processing', message: 'CONNECTING TO API...' });
    },
    onSuccess: (response) => {
      setImportStatus({
        status: 'success',
        message: 'API SYNC COMPLETE',
        imported: response.data.imported,
        failed: response.data.failed,
      });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary'] });
    },
    onError: (error: any) => {
      setImportStatus({
        status: 'error',
        message: error.response?.data?.detail || 'API SYNC FAILED',
      });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
    }
  };

  const handleApiImport = () => {
    if (!apiEndpoint.trim()) {
      setImportStatus({ status: 'error', message: 'API ENDPOINT REQUIRED' });
      return;
    }
    apiImportMutation.mutate();
  };

  const resetStatus = () => {
    setImportStatus({ status: 'idle' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const tabs: { id: ImportType; label: string }[] = [
    { id: 'csv', label: 'CSV' },
    { id: 'excel', label: 'EXCEL' },
    { id: 'api', label: 'API' },
  ];

  return (
    <div className="border border-mono-800">
      <div className="border-b border-mono-800 px-4 py-3 flex items-center justify-between">
        <h3 className="text-xs text-mono-300 tracking-widest">DATA IMPORT</h3>
        {importStatus.status !== 'idle' && (
          <button
            onClick={resetStatus}
            className="text-[10px] text-mono-500 tracking-wider hover:text-mono-300"
          >
            RESET
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-mono-800 flex">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              resetStatus();
            }}
            className={`px-4 py-2 text-xs tracking-widest transition-colors ${
              activeTab === tab.id
                ? 'text-mono-100 border-b border-mono-100 -mb-px'
                : 'text-mono-500 hover:text-mono-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* Status message */}
        {importStatus.status !== 'idle' && (
          <div
            className={`mb-4 p-3 border text-center ${
              importStatus.status === 'error'
                ? 'border-mono-500 bg-mono-900'
                : importStatus.status === 'success'
                ? 'border-mono-600 bg-mono-900'
                : 'border-mono-700'
            }`}
          >
            <p className="text-xs text-mono-300 tracking-wider">{importStatus.message}</p>
            {importStatus.imported !== undefined && (
              <p className="text-[10px] text-mono-500 mt-1">
                {importStatus.imported} IMPORTED
                {importStatus.failed ? `, ${importStatus.failed} FAILED` : ''}
              </p>
            )}
          </div>
        )}

        {/* CSV/Excel Upload */}
        {(activeTab === 'csv' || activeTab === 'excel') && (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept={activeTab === 'csv' ? '.csv' : '.xlsx,.xls'}
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="block w-full py-8 border border-dashed border-mono-700 text-center cursor-pointer hover:border-mono-500 transition-colors"
            >
              <p className="text-xs text-mono-400 tracking-wider">
                {uploadMutation.isPending ? 'UPLOADING...' : `DROP ${activeTab.toUpperCase()} FILE OR CLICK TO BROWSE`}
              </p>
              <p className="text-[10px] text-mono-600 mt-2">
                {activeTab === 'csv' ? 'SUPPORTED: .CSV' : 'SUPPORTED: .XLSX, .XLS'}
              </p>
            </label>

            <div className="mt-4 border border-mono-800 p-3">
              <p className="text-[10px] text-mono-500 tracking-widest mb-2">REQUIRED COLUMNS</p>
              <p className="text-[10px] text-mono-400 leading-relaxed">
                NAME, ADDRESS, CITY, STATE, ZIP, LATITUDE, LONGITUDE
              </p>
              <p className="text-[10px] text-mono-500 tracking-widest mt-3 mb-2">OPTIONAL COLUMNS</p>
              <p className="text-[10px] text-mono-400 leading-relaxed">
                SITE_ID, REGULATORY_PROGRAM, RELEASE_DATE, CONTAMINANTS, AQUIFER_TYPE
              </p>
            </div>
          </div>
        )}

        {/* API Import */}
        {activeTab === 'api' && (
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
                API ENDPOINT
              </label>
              <input
                type="url"
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-sm focus:border-mono-400 focus:outline-none"
                placeholder="https://api.example.com/sites"
              />
            </div>

            <div>
              <label className="block text-[10px] text-mono-500 tracking-widest mb-2">
                API KEY (OPTIONAL)
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="w-full px-3 py-2 bg-mono-900 border border-mono-700 text-mono-200 text-sm focus:border-mono-400 focus:outline-none"
                placeholder="Bearer token or API key"
              />
            </div>

            <button
              onClick={handleApiImport}
              disabled={apiImportMutation.isPending}
              className="w-full py-2 border border-mono-100 bg-mono-100 text-mono-950 text-xs tracking-widest hover:bg-mono-200 disabled:opacity-50 transition-colors"
            >
              {apiImportMutation.isPending ? 'SYNCING...' : 'SYNC FROM API'}
            </button>

            <div className="border border-mono-800 p-3">
              <p className="text-[10px] text-mono-500 tracking-widest mb-2">API REQUIREMENTS</p>
              <p className="text-[10px] text-mono-400 leading-relaxed">
                ENDPOINT MUST RETURN JSON ARRAY WITH SITE OBJECTS CONTAINING: NAME, ADDRESS, CITY, STATE, COORDINATES
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
